import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { FaPaperPlane, FaRobot, FaChevronDown } from "react-icons/fa";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { BASIC_RECOMMENDATION_PROMPTS, TRADER_RECOMMENDATION_PROMPTS } from "./AgentRecommendationPrompts";
import { useInstanceStore } from "../stores/UseInstanceStore";
import { useWallet } from "../hooks/useWallet";
import { fetchModelRegistry, getModelDisplayName } from "../utils/models";


export default function AgentRecommendation({
  config,
  portfolioId = 1,
  onRecommendationGenerated = null, // Callback to refresh recommendations
  portfolio,
}) {
  const { jwtToken } = useWallet();
  // Get prompt mode from store
  const { instance, setPromptMode: setStoredPromptMode } = useInstanceStore();
  const promptMode = instance.promptMode || 'basic';

  // Abort controller ref — cancelled on unmount or when a new request starts
  const abortRef = useRef(null);

  // Cancel any in-flight poll when unmounting
  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  // Storage key for in-progress recommendation ticket (per portfolio)
  const pendingTicketKey = `agentRecommendation_pendingTicket_${portfolioId}`;

  // State management
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // True when we resumed a ticket from a previous tab session (vs. a fresh submit)
  const [isResuming, setIsResuming] = useState(false);

  // Set loading state synchronously before first paint if there's a saved ticket.
  // useLayoutEffect runs before the browser paints, so there's no flash of "not loading".
  useLayoutEffect(() => {
    try {
      if (localStorage.getItem(pendingTicketKey)) {
        setIsLoading(true);
        setIsResuming(true);
      }
    } catch { /* ignore */ }
  }, [pendingTicketKey]);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [lastResponse, setLastResponse] = useState(null);
  const [promptHistory, setPromptHistory] = useState([]);
  const [showTips, setShowTips] = useState(false);
  const [agentModelName, setAgentModelName] = useState(null);
  const [modelRegistry, setModelRegistry] = useState([]);

  // Fetch the model registry from /models API
  useEffect(() => {
    const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');
    fetchModelRegistry(apiBaseUrl).then(setModelRegistry);
  }, [config?.apiBaseUrl]);

  // Fetch the active prompt's model_name for the portfolio's active agent
  useEffect(() => {
    const agentId = portfolio?.active_agent?.id;
    if (!jwtToken || !agentId) { setAgentModelName(null); return; }
    fetch(`${(config?.apiBaseUrl || window.location.origin.replace('/web', ''))}/agents/${agentId}/prompts/active`, {
      headers: { Authorization: `Bearer ${jwtToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setAgentModelName(data?.model_name || null))
      .catch(() => setAgentModelName(null));
  }, [jwtToken, config?.apiBaseUrl, portfolio?.active_agent?.id]);

  // Load prompt history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('agentRecommendationHistory');
    if (savedHistory) {
      try {
        setPromptHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error('Failed to parse prompt history:', e);
        setPromptHistory([]);
      }
    }
  }, []);

  // Save prompt to history
  const savePromptToHistory = (prompt) => {
    if (!prompt.trim()) return;
    
    const newHistory = [
      prompt.trim(),
      ...promptHistory.filter(p => p !== prompt.trim())
    ].slice(0, 10); // Keep only last 10
    
    setPromptHistory(newHistory);
    localStorage.setItem('agentRecommendationHistory', JSON.stringify(newHistory));
  };

  // Handle prompt mode changes with persistence
  const handlePromptModeChange = (newMode) => {
    setStoredPromptMode(newMode);
  };

  // Get current prompt set based on mode
  const getCurrentPrompts = () => {
    if (promptMode === 'history') {
      return promptHistory.map((prompt, index) => ({
        id: `history-${index}`,
        label: prompt.length > 50 ? `${prompt.substring(0, 50)}...` : prompt,
        prompt: prompt,
        category: 'History'
      }));
    }
    return promptMode === 'basic' ? BASIC_RECOMMENDATION_PROMPTS : TRADER_RECOMMENDATION_PROMPTS;
  };

  // Get API base URL from config, fallback to localhost
  const apiBaseUrl = config?.apiBaseUrl || window.location.origin.replace('/web', '');
  const apiUrl = `${apiBaseUrl}/agent/recommendation/message`;

  // Shared poll loop — resolves when done, throws on error/timeout.
  // Saves/clears the pending ticket in localStorage so tab changes don't kill it.
  const runPollLoop = useCallback(async (pollApiUrl, signal, authToken) => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_POLL_ATTEMPTS = 120; // 6 minutes

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      if (signal.aborted) return null;

      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        if (signal.aborted) return null;
      }

      let pollData;
      try {
        const pollResponse = await fetch(pollApiUrl, {
          signal,
          headers: {
            'accept': 'application/json',
            ...(authToken && { Authorization: `Bearer ${authToken}` }),
          },
        });
        if (!pollResponse.ok) {
          console.warn(`Poll attempt ${attempt + 1} returned ${pollResponse.status}, retrying...`);
          continue;
        }
        pollData = await pollResponse.json();
      } catch (fetchErr) {
        if (fetchErr.name === 'AbortError') return null;
        console.warn(`Poll attempt ${attempt + 1} fetch error: ${fetchErr.message}, retrying...`);
        continue;
      }

      console.log(`Poll attempt ${attempt + 1}, status: ${pollData.status}, done: ${pollData.done}`);

      if (!pollData.done) continue;

      if (pollData.error) {
        throw new Error(`Recommendation failed: ${pollData.error}`);
      }

      return pollData.response;
    }

    throw new Error('Recommendation timed out after 6 minutes.');
  }, []);

  // On mount: resume polling if there's an in-progress ticket saved in localStorage
  useEffect(() => {
    const saved = localStorage.getItem(pendingTicketKey);
    if (!saved) return;

    let parsed;
    try { parsed = JSON.parse(saved); } catch { localStorage.removeItem(pendingTicketKey); return; }

    const { pollApiUrl, savedJwtToken } = parsed;
    if (!pollApiUrl) { localStorage.removeItem(pendingTicketKey); return; }

    // Resume with a fresh abort controller
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    console.log(`AgentRecommendation: resuming poll for saved ticket at ${pollApiUrl}`);
    setIsLoading(true);
    setIsResuming(true);
    setError(null);

    runPollLoop(pollApiUrl, signal, savedJwtToken || jwtToken)
      .then((response) => {
        if (response === null) return; // aborted
        localStorage.removeItem(pendingTicketKey);
        setLastResponse(response);
        if (onRecommendationGenerated) onRecommendationGenerated(response);
      })
      .catch((err) => {
        localStorage.removeItem(pendingTicketKey);
        if (err.name !== 'AbortError') setError(err);
      })
      .finally(() => { setIsLoading(false); setIsResuming(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingTicketKey]); // run once per portfolioId mount

  // Handle sending recommendation request with async polling
  const handleSendRecommendation = async () => {
    if (!message.trim()) {
      setError("Please enter a message before sending.");
      return;
    }

    if (!portfolioId || portfolioId === 'null' || portfolioId === 'undefined') {
      setError("No valid portfolio selected. Please wait for portfolio to load or refresh the page.");
      return;
    }

    // Cancel any previous in-flight poll
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setIsLoading(true);
    setError(null);
    setLastResponse(null);

    try {
      // Step 1: Submit message and get ticket_id
      const submitResponse = await fetch(apiUrl, {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          ...(jwtToken && { Authorization: `Bearer ${jwtToken}` }),
        },
        body: JSON.stringify({
          message: message.trim(),
          portfolio_id: portfolioId
        }),
      });

      if (!submitResponse.ok) {
        if (submitResponse.status === 402) {
          let errorDetail = "Recommendation limit exceeded for your subscription plan";
          try {
            const errorData = await submitResponse.json();
            if (errorData.detail) errorDetail = errorData.detail;
          } catch { /* ignore parse error */ }
          throw new Error(`SUBSCRIPTION_LIMIT:${errorDetail}`);
        }
        throw new Error(`Failed to submit recommendation request: ${submitResponse.status} ${submitResponse.statusText}`);
      }

      const submitData = await submitResponse.json();
      const ticketId = submitData.ticket_id;
      const pollApiUrl = `${apiBaseUrl}/agent/recommendation/poll/${ticketId}`;

      console.log(`Got recommendation ticket ID: ${ticketId}, starting polling...`);

      // Persist ticket so polling survives tab changes
      localStorage.setItem(pendingTicketKey, JSON.stringify({ pollApiUrl, savedJwtToken: jwtToken }));

      // Step 2: Poll for results
      const response = await runPollLoop(pollApiUrl, signal, jwtToken);
      if (response === null) return; // aborted

      localStorage.removeItem(pendingTicketKey);
      setLastResponse(response);
      savePromptToHistory(message);
      setMessage('');
      if (onRecommendationGenerated) onRecommendationGenerated(response);

    } catch (err) {
      localStorage.removeItem(pendingTicketKey);
      console.error("Error in recommendation request:", err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle predefined prompt selection
  const handlePromptSelect = (prompt) => {
    setMessage(prompt.prompt);
    setIsDropdownOpen(false);
  };

  // Handle Enter key in textarea (Shift+Enter for new line, Enter to send)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && message.trim()) {
        handleSendRecommendation();
      }
    }
  };

  return (
    <div className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-4">

      {/* No Tokens Warning */}
      {(!portfolio?.tokens_selected || portfolio.tokens_selected.length === 0) && (
        <div className="mb-4 p-4 bg-amber-900/30 border border-amber-700/50 rounded-lg">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h4 className="text-amber-400 font-medium">No Tokens Selected</h4>
              <p className="text-amber-300/80 text-sm mt-1">
                You must select at least one token in your portfolio before generating recommendations. 
                Use the Token Selection panel above to choose which tokens the AI can analyze and recommend trades for.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Input Section */}
      <div className="space-y-3">
        {/* Predefined Prompts Dropdown */}
        <div className="relative">
          {portfolio && portfolio.tokens_selected && portfolio.tokens_selected.length > 0 && (
            <div className="mb-3">
              <span className="text-sm text-slate-400 mr-2">Available Tokens:</span>
              <ul className="inline-flex flex-wrap gap-1.5 mt-1">
                {portfolio.tokens_selected.map((token, idx) => (
                  <li 
                    key={idx}
                    className="px-2.5 py-0.5 bg-cyan-900/40 border border-cyan-700/50 text-cyan-300 text-xs font-medium rounded-full"
                  >
                    {token}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {agentModelName && (
            <div className="mb-3">
              <span className="text-sm text-slate-400 mr-2">Agent Model:</span>
              <span className="px-2.5 py-0.5 bg-purple-900/40 border border-purple-700/50 text-purple-300 text-xs font-medium rounded-full">
                {getModelDisplayName(modelRegistry, agentModelName)}
              </span>
            </div>
          )}
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-slate-300">
              Quick Prompts
            </label>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-400">Mode:</label>
              <div className="flex bg-slate-700/30 rounded-lg p-1 border border-slate-600">
                <button
                  onClick={() => handlePromptModeChange('basic')}
                  disabled={isLoading}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    promptMode === 'basic' 
                      ? 'bg-cyan-600 text-white shadow-sm' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-600/50'
                  }`}
                >
                  Basic
                </button>
                <button
                  onClick={() => handlePromptModeChange('trader')}
                  disabled={isLoading}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    promptMode === 'trader' 
                      ? 'bg-cyan-600 text-white shadow-sm' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-600/50'
                  }`}
                >
                  Pro
                </button>
                <button
                  onClick={() => handlePromptModeChange('history')}
                  disabled={isLoading}
                  className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                    promptMode === 'history' 
                      ? 'bg-cyan-600 text-white shadow-sm' 
                      : 'text-slate-300 hover:text-white hover:bg-slate-600/50'
                  }`}
                >
                  History
                </button>
              </div>
            </div>
          </div>
          <div className="relative">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="w-full bg-slate-700/50 border border-slate-600 rounded-lg px-3 py-2 text-left text-slate-300 hover:bg-slate-700/70 transition-colors flex items-center justify-between"
              disabled={isLoading}
            >
              <span>Select a predefined prompt...</span>
              <FaChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-slate-700 border border-slate-600 rounded-lg shadow-lg max-h-80 overflow-y-auto">
                {getCurrentPrompts().length === 0 && promptMode === 'history' ? (
                  <div className="px-3 py-4 text-center text-slate-400">
                    <div className="text-sm">No prompt history available</div>
                    <div className="text-xs mt-1">Your recent prompts will appear here</div>
                  </div>
                ) : (
                  getCurrentPrompts().map((prompt) => (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptSelect(prompt)}
                      className="w-full px-3 py-2 text-left text-slate-300 hover:bg-slate-600 transition-colors first:rounded-t-lg last:rounded-b-lg border-b border-slate-600/50 last:border-b-0"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-medium">{prompt.label}</div>
                        {prompt.category && (
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            prompt.category === 'History' 
                              ? 'bg-purple-800/50 text-purple-300'
                              : 'bg-slate-800/50 text-slate-400'
                          }`}>
                            {prompt.category}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-400 truncate mt-1">
                        {prompt.prompt.length > 100 ? `${prompt.prompt.substring(0, 100)}...` : prompt.prompt}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Custom Message Textarea */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1">
            Agent Instructions
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Describe what kind of trading recommendations you're looking for... (Press Enter to send, Shift+Enter for new line)"
            className="w-full bg-slate-700/50 border border-slate-600 px-3 py-3 text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-y transition-colors font-mono text-base sm:text-lg"
            rows={4}
            disabled={isLoading}
          />
        </div>

        {/* Send Button */}
        <button
          onClick={handleSendRecommendation}
          disabled={isLoading || !message.trim()}
          className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
            isLoading || !message.trim()
              ? 'bg-slate-600/50 text-slate-400 cursor-not-allowed'
              : 'bg-cyan-600 hover:bg-cyan-700 text-white'
          }`}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Generating Recommendation...
            </>
          ) : (
            <>
              <FaPaperPlane className="w-4 h-4" />
              Generate Recommendation
            </>
          )}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`mt-4 p-3 border rounded-lg ${
          (typeof error === 'string' && error.startsWith('SUBSCRIPTION_LIMIT:')) || 
          (error.message && error.message.startsWith('SUBSCRIPTION_LIMIT:'))
            ? 'bg-orange-900/30 border-orange-800'
            : 'bg-red-900/30 border-red-800'
        }`}>
          <div className="flex items-start">
            <svg className={`w-5 h-5 mr-2 flex-shrink-0 mt-0.5 ${
              (typeof error === 'string' && error.startsWith('SUBSCRIPTION_LIMIT:')) || 
              (error.message && error.message.startsWith('SUBSCRIPTION_LIMIT:'))
                ? 'text-orange-400'
                : 'text-red-400'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {(typeof error === 'string' && error.startsWith('SUBSCRIPTION_LIMIT:')) || 
               (error.message && error.message.startsWith('SUBSCRIPTION_LIMIT:')) ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 0h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              )}
            </svg>
            <div className="flex-1">
              {(typeof error === 'string' && error.startsWith('SUBSCRIPTION_LIMIT:')) || 
               (error.message && error.message.startsWith('SUBSCRIPTION_LIMIT:')) ? (
                <>
                  <h4 className="text-orange-400 font-medium">Subscription Limit Reached</h4>
                  <p className="text-orange-300 text-sm mt-1">
                    {typeof error === 'string' 
                      ? error.replace('SUBSCRIPTION_LIMIT:', '') 
                      : error.message.replace('SUBSCRIPTION_LIMIT:', '')
                    }
                  </p>
                  <div className="mt-3">
                    <a 
                      href="/plans" 
                      className="inline-flex items-center px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Upgrade Your Plan
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="text-red-400 font-medium">Error</h4>
                  <p className="text-red-300 text-sm mt-1">
                    {typeof error === 'string' ? error : error.message || 'An unexpected error occurred'}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Response Display */}
      {lastResponse && !error && (
        <div className="mt-4 p-3 bg-green-900/30 border border-green-800 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <div>
              <h4 className="text-green-400 font-medium">Recommendation Generated</h4>
              <p className="text-green-300 text-sm mt-1">
                New trading recommendation has been generated successfully. Check the recommendations list above for details.
              </p>
              {lastResponse.portfolio_name && (
                <p className="text-green-200/80 text-xs mt-2">
                  Portfolio: {lastResponse.portfolio_name}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Loading Progress Indicator */}
      {(isLoading || !!localStorage.getItem(pendingTicketKey)) && (
        <div className="mt-4 p-3 bg-cyan-900/30 border border-cyan-800 rounded-lg">
          <div className="flex items-center">
            <svg className="animate-spin h-5 w-5 text-cyan-400 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <div>
              <p className="text-cyan-400 font-medium">
                {(isResuming || !!localStorage.getItem(pendingTicketKey)) ? 'Recommendation in progress...' : 'AI is analyzing market conditions...'}
              </p>
              <p className="text-cyan-300/80 text-sm">
                {(isResuming || !!localStorage.getItem(pendingTicketKey))
                  ? 'A recommendation was started in another tab and is still being generated.'
                  : 'This may take up to 2 minutes while we gather data and generate recommendations.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Usage Tips */}
      <div className="mt-4 p-3 bg-slate-700/30 border border-slate-600/50 rounded-lg">
        <button
          onClick={() => setShowTips(!showTips)}
          className="w-full flex items-center justify-between text-slate-300 font-medium text-sm hover:text-white transition-colors"
        >
          <span>💡 Tips for better recommendations</span>
          {showTips ? (
            <FiEyeOff className="w-4 h-4 text-slate-400 hover:text-slate-300" />
          ) : (
            <FiEye className="w-4 h-4 text-slate-400 hover:text-slate-300" />
          )}
        </button>
        {showTips && (
          <ul className="text-slate-400 text-xs space-y-1 mt-2 pt-2 border-t border-slate-600/30">
            <li>• Toggle between Basic and Trader prompts for different complexity levels</li>
            <li>• Be specific about your risk tolerance and time horizon</li>
            <li>• Mention specific assets or market conditions you're interested in</li>
            <li>• Include your preferred trading style (scalping, swing trading, etc.)</li>
            <li>• Specifics on trade type and token type is allowed.</li>
            <li>• Read documentation for advanced features and options.</li>
          </ul>
        )}
      </div>
    </div>
  );
}
